import { createSelector } from "reselect";
import { isDefined, compareValues, uniqueByKey, memoize } from "@ls/js-utils";
import SelectionState from "state/selections/SelectionState";
import { collectEntitySummaryInfo, splitByCalcType } from "state/util";
import scope from "./scope";
import SummaryType from "state/selections/Constants/SummaryType";

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return (state) => state[scope]["definitions"][selectionState];
  }

  return createSelector(
    (state) => state[scope]["definitions"][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(
        ({ complete }) => complete
      );
      return completeDefinitions.filter(
        uniqueByKey(completeDefinitions, "key")
      );
    }
  );
});

export const selectBookAnalyticsData = (summaryType) => (state) =>
  state[scope]["bookAnalyticsData"][summaryType];
export const selectFetchingBookAnalyticsData = (summaryType) => (state) =>
  state[scope]["fetchingBookAnalyticsData"][summaryType];

export const selectRowsMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectBookAnalyticsData(SummaryType.ABSOLUTE),
  selectBookAnalyticsData(SummaryType.RELATIVE),
  (definitions, absData, relData) => {
    const absEntitySummaries = absData?.root?.entitySummaries ?? [];
    const relEntitySummaries = relData?.root?.entitySummaries ?? [];
    const { bmkEntityIdSet: absBmkIdSet, bmkSummaryMap: bmkIdSet } =
      collectEntitySummaryInfo(absEntitySummaries);
    const {
      entitySummaryMap: relEntitySummaryMap,
      bmkEntityIdSet: relBmkIdSet,
    } = collectEntitySummaryInfo(relEntitySummaries);
    const bmkEntityIdSet = new Set([...absBmkIdSet, ...relBmkIdSet]);
    const [absDefinitions, relDefinitions, bmkDefinitions] =
      splitByCalcType(definitions);
    const entityRowsMap = {};

    /**Functions for handling abs/rel definitions**/
    const handleAbsDefinitions = (definitions, entitySummaries, scenario) => {
      definitions.forEach((definition) => {
        const { bookAnalytic, basis, summary } = definition;
        entitySummaries.forEach((entitySummary) => {
          const { amortBooks, entityResultMetaData } = entitySummary;
          const { entityId } = entityResultMetaData;
          const colId = `${bookAnalytic},${basis},${summary}`;

          if (!entityRowsMap[entityId]) {
            entityRowsMap[entityId] = {};
          }
          const findBasesBookAnalytic = amortBooks.find(
            (ab) => ab.costBasis === basis
          );

          entityRowsMap[entityId][colId] =
            findBasesBookAnalytic?.values?.[bookAnalytic];
        });
      });
    };
    const handleRelDefinitions = (definitions, entitySummaries, scenario) => {
      definitions.forEach((definition) => {
        const { bookAnalytic, basis, summary } = definition;
        entitySummaries.forEach((entitySummary) => {
          const { amortBooks, entityResultMetaData } = entitySummary;
          const { entityId } = entityResultMetaData;
          const colId = `${bookAnalytic},${basis},${summary}`;
          if (!entityRowsMap[entityId]) {
            entityRowsMap[entityId] = {};
          }
          if (
            bmkEntityIdSet.has(entityId) &&
            isDefined(entityRowsMap[entityId][colId])
          ) {
            //for benchmarks, make sure value doesn't exist already since we want to honor absolute values first
            return;
          }
          /* if (!!relative) { */
          const findBasesBookAnalytic = amortBooks.find(
            (ab) => ab.costBasis === basis
          );

          entityRowsMap[entityId][colId] =
            findBasesBookAnalytic?.values?.[bookAnalytic];
          /*  } */
        });
      });
    };

    /**Handle abs and rel definitions**/
    handleAbsDefinitions(absDefinitions, absEntitySummaries);
    handleRelDefinitions(relDefinitions, relEntitySummaries);

    /**now handle benchmark definitions**/
    bmkDefinitions.forEach((definition) => {
      const { analytic, summary } = definition;
      [...absEntitySummaries, ...relEntitySummaries].forEach(
        (entitySummary) => {
          const { entityResultMetaData } = entitySummary;
          const { entityId, benchId } = entityResultMetaData;
          const colId = `${analytic},${summary}`;

          if (!entityRowsMap[entityId]?.[colId]) {
            const bmkEntitySummary =
              bmkIdSet.get(benchId) ?? relEntitySummaryMap.get(benchId);
            if (!entityRowsMap[entityId]) {
              entityRowsMap[entityId] = {};
            }
            if (
              bmkEntityIdSet.has(entityId) &&
              isDefined(entityRowsMap[entityId]?.[colId])
            ) {
              //for benchmarks, make sure value doesn't exist already since we want to honor absolute values first
              return;
            }
            entityRowsMap[entityId][colId] =
              bmkEntitySummary?.values?.[analytic] ?? null;
          }
        }
      );
    });
    return entityRowsMap;
  }
);

export const selectFetchingBookBasisValues = (state) =>
  state[scope].fetchingBookBasisValues;
export const selectBookBasisValues = (state) => state[scope].bookBasisValues;
export const selectBookBasisValuesSelectionOptions = createSelector(
  selectBookBasisValues,
  (bookBasisValues) => {
    console.log("Book Basis Values:", bookBasisValues);
    if (bookBasisValues && bookBasisValues.length > 0) {
      return bookBasisValues
        .map((aValue) => ({ label: aValue.toUpperCase(), value: aValue }))
        .sort(compareValues("label"));
    }
    return [];
  }
);
